<template>
  <section class="invoice-preview-wrapper">
    <!-- Modal de Stock Insuficiente → conversión parcial con registro de venta caída -->
    <b-modal
      id="modal-stock-issues"
      title="Stock insuficiente"
      centered
      size="lg"
      cancel-title="Cancelar"
      cancel-variant="outline-dark"
      :ok-title="stockSummary && !stockSummary.order_would_be_created ? 'Registrar venta caída sin pedido' : 'Generar pedido con lo disponible'"
      ok-variant="warning"
      :ok-disabled="isConvertingPartial"
      @ok="convertPartial"
    >
      <div
        v-if="stockIssues"
        class="mt-1"
      >
        <div class="text-center">
          <h5 class="text-danger mb-1">
            <feather-icon
              icon="AlertTriangleIcon"
              size="18"
              class="mr-50"
            />
            No hay suficiente stock para atender toda la cotización
          </h5>
        </div>
        <div class="table-responsive">
          <table class="table table-bordered table-striped table-sm">
            <thead class="thead-dark">
              <tr class="text-center">
                <th>Producto</th>
                <th>Solicitado</th>
                <th>Disponible</th>
                <th>A atender</th>
                <th>Venta caída</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="issue in stockIssues"
                :key="issue.product_variant_id || issue.name"
              >
                <td class="font-weight-bold">
                  {{ issue.name }}
                </td>
                <td class="text-center">
                  {{ issue.requested }}
                </td>
                <td class="text-center">
                  {{ issue.available }}
                </td>
                <td class="text-center text-success font-weight-bold">
                  {{ issue.to_fulfill }}
                </td>
                <td class="text-center text-danger font-weight-bold">
                  {{ issue.lost }}
                </td>
              </tr>
            </tbody>
            <tfoot v-if="stockSummary">
              <tr class="font-weight-bold">
                <td>
                  Total ({{ stockSummary.lineas_caidas }} de {{ stockSummary.lineas }} líneas con faltante)
                </td>
                <td class="text-center">
                  {{ stockSummary.solicitado }}
                </td>
                <td />
                <td class="text-center text-success">
                  {{ stockSummary.atendido }}
                </td>
                <td class="text-center text-danger">
                  {{ stockSummary.caido }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <!-- Monto solo para administración: el backend lo omite para vendedores -->
        <p
          v-if="stockSummary && stockSummary.monto_caido != null"
          class="mb-50"
        >
          <span class="font-weight-bold">Monto de venta caída:</span>
          {{ stockSummary.moneda }}{{ Number(stockSummary.monto_caido).toFixed(2) }}
        </p>
        <p
          v-if="stockSummary && !stockSummary.order_would_be_created"
          class="text-danger mb-50"
        >
          Ninguna línea tiene stock disponible: no se generará pedido y la cotización quedará en estado <b>Venta Caída</b>.
        </p>
        <div class="text-muted mt-1">
          <small>
            <feather-icon
              icon="InfoIcon"
              size="18"
              class="cursor-pointer"
            />
            Al confirmar, el pedido se crea sólo con las cantidades disponibles y el faltante queda registrado como venta caída.
            Las cantidades pueden variar si el inventario cambia antes de confirmar.
            También puede cancelar y editar la cotización, o esperar reposición de stock.
          </small>
        </div>
      </div>
    </b-modal>

    <!-- Modal de Flujo de Estados -->
    <b-modal
      id="modal-status-flow"
      title="Flujo de Estados en una Cotización"
      ok-only
      ok-title="Entendido"
      ok-variant="primary"
      centered
      size="lg"
    >
      <div class="status-flow-diagram">
        <div class="status-row">
          <div class="status-box info">
            En Proceso
            <small class="d-block text-muted">Estado inicial</small>
          </div>
          <div class="arrow">
            →
          </div>
          <div class="status-box secondary">
            Enviado
            <small class="d-block text-muted">En revisión del cliente</small>
          </div>
        </div>

        <div class="status-row mt-2">
          <div class="status-box secondary invisible">
            Placeholder
          </div>
          <div
            class="arrow"
            style="transform: rotate(90deg)"
          >
            →
          </div>
        </div>

        <div class="status-row">
          <div class="status-box success">
            Aceptado
            <small class="d-block text-muted">Aprobado por cliente</small>
          </div>
          <div class="arrow">
            ←
          </div>
          <div class="status-box warning">
            Correcciones
            <small class="d-block text-muted">Requiere cambios</small>
          </div>
          <div class="arrow">
            ←
          </div>
          <div class="status-box danger">
            Declinado
            <small class="d-block text-muted">Rechazado por cliente</small>
          </div>
        </div>

        <div class="status-row mt-2">
          <div
            class="arrow"
            style="transform: rotate(90deg)"
          >
            →
          </div>
        </div>

        <div class="status-row">
          <div class="status-box dark">
            Pedido
            <small class="d-block text-muted">Convertido a pedido</small>
          </div>
          <div class="arrow">
            /
          </div>
          <div class="status-box danger">
            Venta Caída
            <small class="d-block text-muted">Sin stock para ninguna línea</small>
          </div>
        </div>

        <div class="mt-3 text-muted">
          <small>
            <feather-icon
              icon="InfoIcon"
              size="18"
              class="cursor-pointer mr-1"
            />
            La cotización puede ser cancelada en cualquier momento antes de convertirse en pedido
          </small>
        </div>
      </div>
    </b-modal>

    <b-row class="invoice-add">

      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          class="invoice-preview-card"
          no-body
        >
          <b-card-body>
            <!-- Estado de la cotización -->
            <b-row>
              <b-col
                cols="12"
                md="12"
                class="mb-1"
              >
                <h3>
                  <b-badge
                    :variant="getStatusVariant(customerQuoteData.Estado)"
                    pill
                  >
                    #{{ customerQuoteData.Id }} - {{ customerQuoteData.Estado }}
                  </b-badge>
                  <feather-icon
                    v-b-tooltip.hover
                    icon="HelpCircleIcon"
                    size="18"
                    class="cursor-pointer ml-1"
                    title="Ver flujo de estados"
                    @click="$bvModal.show('modal-status-flow')"
                  />
                  <b-badge
                    v-if="customerQuoteData.VentaCaida"
                    v-b-tooltip.hover
                    variant="light-danger"
                    pill
                    class="ml-50"
                    :title="`Se atendieron ${customerQuoteData.VentaCaida.atendido_total} de ${customerQuoteData.VentaCaida.solicitado_total} unidades solicitadas`"
                  >
                    Venta caída: {{ customerQuoteData.VentaCaida.unidades }} u.<span v-if="customerQuoteData.VentaCaida.monto"> ({{ getCurrencySymbol }}.{{ customerQuoteData.VentaCaida.monto }})</span>
                  </b-badge>
                </h3>
                <span v-if="customerQuoteData.Estado === 'Pedido'">
                  <b-link
                    style="font-size: 1.05rem; margin-left: 4px;"
                    @click="$router.push({ name: 'sales-customer-orders', params: { id: customerQuoteData.Pedido } })"
                  >
                    <u>Ir al pedido #{{ customerQuoteData.Pedido }}</u>
                  </b-link>
                </span>
              </b-col>
            </b-row>

            <b-row>
              <b-col
                cols="12"
                xl="12"
                md="12"
              >
                <p
                  style="font-size: 13.5px;"
                >
                  <!-- Ingresado -->
                  <span class="font-weight-bold">Colocada por:</span>
                  {{ customerQuoteData.UsuarioNombre }} <b>el:</b> {{ customerQuoteData.FechaIngresoSistema }}
                  <br>
                  <span class="font-weight-bold">Fecha de la cotización:</span>
                  {{ customerQuoteData.FechaCotizacionCliente }}
                  <br>
                  <!-- Validez -->
                  <span class="font-weight-bold">Válida hasta:</span>
                  {{ customerQuoteData.FechaVencimiento }}
                  <br>
                  <span class="font-weight-bold">Lista de precios:</span>
                  {{ (customerQuoteData.PriceListInfo && customerQuoteData.PriceListInfo.name) ? customerQuoteData.PriceListInfo.name : 'No asignada' }}
                  <br>
                </p>
              </b-col>
            </b-row>

            <!-- Info de cliente -->
            <b-row class="mt-1">
              <b-col
                cols="12"
                xl="12"
                md="12"
              >
                <h3>#{{ customerQuoteData.CustomerInfo.id }} - {{ customerQuoteData.CustomerInfo.name }}</h3>
              </b-col>
            </b-row>

            <b-row>
              <b-col
                cols="12"
                xl="6"
                md="6"
              >
                <p>
                  <!-- NIT -->
                  <span class="font-weight-bold">NIT:</span>
                  {{ (customerQuoteData.CustomerInfo.nit_cf === 'Y') ? `Consumidor final - ${customerQuoteData.CustomerInfo.nit}` : customerQuoteData.CustomerInfo.nit }}
                  <br>
                  <!-- Celular -->
                  <span class="font-weight-bold">Celular:</span>
                  {{ (customerQuoteData.CustomerInfo.cellphone) ? customerQuoteData.CustomerInfo.cellphone : '' }}
                  <br>
                  <!-- Teléfono -->
                  <span class="font-weight-bold">Teléfono:</span>
                  {{ (customerQuoteData.CustomerInfo.phone) ? customerQuoteData.CustomerInfo.phone : '' }}
                </p>
              </b-col>
              <b-col
                cols="12"
                xl="6"
                md="6"
              >
                <!-- Dirección -->
                <span class="font-weight-bold">Dirección:</span>
                {{ customerQuoteData.DireccionEnvio }}
              </b-col>
            </b-row>
            <!-- Tipo de venta -->
            <b-row class="mt-1">
              <b-col
                cols="12"
                xl="12"
                md="12"
              >
                <span class="font-weight-bold">Tipo de venta:</span>
                {{ (customerQuoteData.OriginSale) ? customerQuoteData.OriginSale : 'Cliente Final' }}
              </b-col>
            </b-row>

            <b-row class="mt-1">
              <b-col
                cols="12"
                xl="12"
                md="12"
              >
                <!-- Observaciones -->
                <!-- <span class="font-weight-bold">Observaciones de cliente:</span>
                {{ (customerQuoteData.CustomerInfo.observaciones) ? customerQuoteData.CustomerInfo.observaciones : '' }}
                <br> -->
                <span class="font-weight-bold">Observaciones:</span>
                {{ (customerQuoteData.Observaciones) ? customerQuoteData.Observaciones : 'No hay observaciones' }}
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Detalle de la cotización -->
          <b-card-body class="invoice-padding form-item-section">
            <b-table
              responsive
              :items="customerQuoteData.Detalle"
              :fields="quoteFields"
            >
              <template #cell(cantidad_caida)="data">
                <span :class="{ 'text-danger font-weight-bold': Number(data.item.cantidad_caida) > 0 }">
                  {{ data.item.cantidad_caida }}
                </span>
              </template>
              <template #cell(cantidad_cubierta)="data">
                <span :class="{ 'text-warning font-weight-bold': Number(data.item.cantidad_cubierta) < Number(data.item.cantidad) }">
                  {{ data.item.cantidad_cubierta }}
                </span>
              </template>
              <template #cell(producto_imagen)="data">
                <img
                  :src="getImageUrl(data.item.producto_imagen ? data.item.producto_imagen.path : null)"
                  :alt="data.item.producto_imagen ? data.item.producto_imagen.alt_text : ''"
                  class="img-fluid cursor-pointer"
                  style="width: 60px; height: 60px; border-color: #d8dee9; border-width: 1px; border-style: solid;"
                  @error="handleImageError"
                  @click="data.item.producto_imagen && ($bvModal.show('modal-image'), selectedImage = data.item.producto_imagen)"
                >
              </template>
              <template #cell(precio_cotizacion)="data">
                {{ getCurrencySymbol }}.{{ data.item.precio_cotizacion }}
              </template>
              <template #cell(total)="data">
                {{ getCurrencySymbol }}.{{ data.item.total }}
              </template>
            </b-table>
            <p
              v-if="customerQuoteData.Cobertura"
              class="text-muted mb-1"
            >
              <feather-icon
                icon="FileTextIcon"
                size="14"
                class="mr-25"
              />
              <template v-if="customerQuoteData.Cobertura.pdf_refleja === 'pedido'">
                El PDF refleja lo atendido en el pedido: {{ customerQuoteData.Cobertura.unidades_cubiertas }} unidades por {{ getCurrencySymbol }}.{{ customerQuoteData.Cobertura.total_cubierto }}.
              </template>
              <template v-else>
                El PDF imprime lo que se puede cubrir con el stock actual: {{ customerQuoteData.Cobertura.unidades_cubiertas }} unidades por {{ getCurrencySymbol }}.{{ customerQuoteData.Cobertura.total_cubierto }}.
                <span
                  v-if="customerQuoteData.Cobertura.unidades_no_cubiertas > 0"
                  class="text-warning font-weight-bold"
                >
                  {{ customerQuoteData.Cobertura.unidades_no_cubiertas }} unidades solicitadas sin cobertura no se imprimen.
                </span>
              </template>
            </p>
            <div
              v-if="customerQuoteData.SummaryInfo[0]"
              class="float-right mr-4"
            >
              <p
                v-if="totalUnidades > 0"
                class="mb-1"
              >
                <span class="font-weight-bold">({{ totalUnidades }} Unidades)</span>
              </p>
              Total:
              <h4 _class="sale-total-amount">
                {{ getCurrencySymbol }}.{{ customerQuoteData.SummaryInfo[0].total }}
              </h4>
            </div>
          </b-card-body>
        </b-card>

        <b-card
          class="invoice-preview-card"
          no-body
        >
          <b-card-body>
            <!-- Estado de la cotización -->
            <b-row>
              <b-col
                cols="12"
                md="12"
              >
                <!-- Textarea para agregar comentarios -->
                <b-form-group
                  class="mb-0"
                >
                  <b-form-textarea
                    id="commentText"
                    v-model="commentText"
                    placeholder="Agregar comentario"
                  />

                  <!-- boton alineado a la derecha -->
                  <b-button
                    variant="outline-primary"
                    :disabled="commentText.length === 0 || disabledAction === 'disabled'"
                    class="float-right mt-1"
                    @click="addComment"
                  >
                    Comentar
                  </b-button>
                </b-form-group>

                <h3
                  v-if="(customerQuoteData.Comentarios.length > 0)"
                  class="font-weight-bold mt-2"
                >
                  {{ customerQuoteData.Comentarios.length }} Comentarios:
                </h3>

                <div
                  v-for="(comment, index) in customerQuoteData.Comentarios"
                  :key="index"
                  class="mt-1"
                >
                  <!-- Si el comentario es tipo = user -->
                  <p v-if="comment.Tipo === 'Usuario'">
                    <span class="font-weight-bold">
                      {{ comment.UsuarioNombre }}
                    </span>
                    <span
                      v-b-tooltip.hover
                      class="float-right font-italic"
                      :title="comment.FechaExacta"
                    >
                      {{ comment.FechaRelativa }}
                    </span>
                    <br>
                    {{ comment.Comentario }}
                  </p>

                  <p v-if="comment.Tipo === 'Sistema'">
                    <span class="text-info">
                      <b>{{ comment.UsuarioNombre }}</b> realizó una acción:
                    </span>
                    <span
                      v-b-tooltip.hover
                      class="text-info float-right font-italic"
                      :title="comment.FechaExacta"
                    >
                      {{ comment.FechaRelativa }}
                    </span>
                    <br>
                    <span class="text-info">
                      {{ comment.Comentario }}
                    </span>
                  </p>

                  <!-- Spacer -->
                  <hr class="invoice-spacing">
                </div>

              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        v-if="customerQuoteData.Estado === 'En Proceso' || customerQuoteData.Estado === 'Enviado' || customerQuoteData.Estado === 'Correcciones' || customerQuoteData.Estado === 'Aceptado' || customerQuoteData.Estado === 'Pedido' || customerQuoteData.Estado === 'Venta Caída'"
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions mt-md-0 mt-2"
      >
        <!-- Action Buttons -->
        <b-card>
          <feather-icon
            v-if="customerQuoteData.Estado === 'En Proceso' || customerQuoteData.Estado === 'Correcciones' || customerQuoteData.Estado === 'Aceptado'"
            v-b-tooltip.hover
            icon="Edit2Icon"
            size="26"
            class="cursor-pointer mr-1 mb-1"
            :title="getEditButtonTooltip(customerQuoteData.Estado)"
            @click="$emit('addEditTab')"
          />
          <!-- Button: Mostrar PDF de la cotización (imprime sólo lo que se puede cubrir) -->
          <feather-icon
            v-if="pdfUnidades > 0"
            v-b-tooltip.hover
            icon="PrinterIcon"
            size="26"
            class="cursor-pointer mb-1"
            :title="`Imprimir cotización (${pdfUnidades} unidades cubiertas)`"
            @click="generarPDF()"
          />
          <!-- Button: Enviar -->
          <b-button
            v-if="customerQuoteData.Estado === 'En Proceso'"
            v-ripple.400="'rgba(255, 159, 67, 0.15)'"
            variant="outline-secondary"
            block
            @click="changeStatus('Enviado')"
          >
            Marcar como enviada
          </b-button>

          <b-button
            v-if="customerQuoteData.Estado === 'Enviado'"
            v-ripple.400="'rgba(255, 159, 67, 0.15)'"
            variant="outline-success"
            block
            @click="changeStatus('Aceptado')"
          >
            Aceptar
          </b-button>
          <b-button
            v-if="customerQuoteData.Estado === 'Enviado'"
            v-ripple.400="'rgba(255, 159, 67, 0.15)'"
            variant="outline-danger"
            block
            @click="changeStatus('Declinado')"
          >
            Declinar
          </b-button>

          <b-button
            v-if="customerQuoteData.Estado === 'En Proceso' || customerQuoteData.Estado === 'Enviado'"
            v-ripple.400="'rgba(255, 159, 67, 0.15)'"
            variant="outline-warning"
            block
            @click="changeStatus('Correcciones')"
          >
            Corregir
          </b-button>

          <b-button
            v-if="customerQuoteData.Estado === 'Aceptado'"
            v-ripple.400="'rgba(255, 159, 67, 0.15)'"
            variant="outline-dark"
            block
            @click="changeStatus('Pedido')"
          >
            Generar pedido
          </b-button>

        </b-card>
      </b-col>
    </b-row>

    <!-- Modal para mostrar imagen en grande -->
    <b-modal
      id="modal-image"
      hide-footer
      centered
      size="lg"
      :title="selectedImage.alt_text"
    >
      <div class="text-center">
        <img
          :src="getImageUrl(selectedImage.path)"
          :alt="selectedImage.alt_text"
          class="img-fluid"
          @error="handleImageError"
        >
      </div>
    </b-modal>

  </section>
</template>

<script>
import axios from '@axios'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import Ripple from 'vue-ripple-directive'
import QuoteGenerator from '@/services/pdf/quoteGenerator'
// import PDFEmailService from '@/services/pdf/pdfEmailService'

export default {

  directives: {
    Ripple,
  },

  props: {
    quoteId: {
      type: Number,
      default: () => 0,
    },
  },

  data() {
    return {
      customerQuoteData: {
        Id: '',
        Estado: '',
        Logotipo: '',
        Flete: '',
        IVA: '',
        FechaCotizacionCliente: '',
        FechaIngresoSistema: '',
        FechaVencimiento: '',
        UsuarioNombre: '',
        SummaryInfo: [],
        Detalle: [],
        Comentarios: [],
        Observaciones: '',
        Vendedor: '',
        VendedorEmail: '',
        Pedido: '',
        VentaCaida: null,
        Cobertura: null,
        CustomerInfo: [],
        PriceListInfo: null,
        CurrencyInfo: null,
      },

      anulated: false,
      showPrint: false,

      commentText: '',
      disabledAction: 'enabled',

      stockIssues: null,
      stockSummary: null,
      isConvertingPartial: false,

      selectedImage: {
        path: '',
        alt_text: '',
      },

      // Price List
      priceListInfo: null,
    }
  },

  computed: {
    getCurrencySymbol() {
      if (this.customerQuoteData.CurrencyInfo && this.customerQuoteData.CurrencyInfo.symbol) {
        return this.customerQuoteData.CurrencyInfo.symbol
      }
      return 'Q'
    },

    totalUnidades() {
      if (!this.customerQuoteData.Detalle || !this.customerQuoteData.Detalle.length) {
        return 0
      }
      return this.customerQuoteData.Detalle.reduce((sum, item) => sum + (Number(item.cantidad) || 0), 0)
    },

    showLostSaleColumns() {
      return !!this.customerQuoteData.VentaCaida
    },

    // Cotización abierta con unidades que el stock actual no cubre: se muestra lo que imprimirá el PDF
    showCoverageColumn() {
      const c = this.customerQuoteData.Cobertura
      return !this.showLostSaleColumns && !!c && c.pdf_refleja === 'stock_actual' && c.unidades_no_cubiertas > 0
    },

    pdfUnidades() {
      const c = this.customerQuoteData.Cobertura
      return c ? Number(c.unidades_cubiertas) || 0 : this.totalUnidades
    },

    quoteFields() {
      const fields = [
        { key: 'producto_imagen', label: 'Imagen' },
        { key: 'sku', label: 'Producto' },
        { key: 'producto_desc', label: 'Descripción' },
        { key: 'cantidad', label: (this.showLostSaleColumns || this.showCoverageColumn) ? 'Solicitado' : 'Cantidad' },
      ]
      if (this.showLostSaleColumns) {
        fields.push({ key: 'cantidad_atendida', label: 'Atendido' })
        fields.push({ key: 'cantidad_caida', label: 'Venta caída' })
      } else if (this.showCoverageColumn) {
        fields.push({ key: 'cantidad_cubierta', label: 'Cubre (PDF)' })
      }
      fields.push({ key: 'precio_cotizacion', label: 'Precio' })
      fields.push({ key: 'total', variant: 'success' })
      return fields
    },
  },

  mounted() {
    if (this.quoteId > 0) {
      this.getCustomerQuoteData()
    }
  },

  methods: {
    async generarPDF() {
      try {
        const generator = new QuoteGenerator(this.customerQuoteData)
        const blob = await generator.generate()

        // Crear un enlace de descarga
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `CotizacionDutton_${this.customerQuoteData.Id}.pdf`
        link.click()
      } catch (error) {
        this.showErrors(error)
      }
    },

    getCustomerQuoteData() {
      axios
        .get(`customer-quote/${this.quoteId}`)
        .then(response => {
          this.customerQuoteData.Id = response.data.data.Id
          this.customerQuoteData.Observaciones = (response.data.data.Observaciones) ? response.data.data.Observaciones : '-'
          this.customerQuoteData.Detalle = response.data.data.Detalle
          this.customerQuoteData.SummaryInfo = response.data.data.SummaryInfo
          this.customerQuoteData.FechaIngresoSistema = response.data.data.FechaIngresoSistema
          this.customerQuoteData.FechaCotizacionCliente = response.data.data.FechaCotizacionCliente
          this.customerQuoteData.FechaVencimiento = response.data.data.FechaVencimiento
          this.customerQuoteData.UsuarioNombre = response.data.data.UsuarioNombre
          this.customerQuoteData.Logotipo = response.data.data.Logotipo
          this.customerQuoteData.Flete = response.data.data.Flete
          this.customerQuoteData.IVA = response.data.data.IVA
          this.customerQuoteData.Estado = response.data.data.Estado
          this.customerQuoteData.Comentarios = response.data.data.Comentarios
          this.customerQuoteData.Observaciones = response.data.data.Observaciones
          this.customerQuoteData.Vendedor = response.data.data.UsuarioNombre
          this.customerQuoteData.VendedorEmail = response.data.data.UsuarioEmail
          this.customerQuoteData.Pedido = response.data.data.Pedido
          this.customerQuoteData.VentaCaida = response.data.data.VentaCaida || null
          this.customerQuoteData.Cobertura = response.data.data.Cobertura || null
          this.customerQuoteData.CustomerInfo = response.data.data.CustomerInfo
          this.customerQuoteData.DireccionEnvio = response.data.data.DireccionEnvio
          this.customerQuoteData.DireccionEnvioId = response.data.data.DireccionEnvioId
          this.customerQuoteData.PriceListInfo = response.data.data.PriceListInfo
          this.customerQuoteData.CurrencyInfo = response.data.data.CurrencyInfo

          if (response.data.data.Estado === 'Cancelado') {
            this.anulated = true
          }
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    getCurrentDateTime() {
      const date = new Date()
      const dateStr = `${(`00${date.getMonth() + 1}`).slice(-2)}/${
        (`00${date.getDate()}`).slice(-2)}/${
        date.getFullYear()} ${
        (`00${date.getHours()}`).slice(-2)}:${
        (`00${date.getMinutes()}`).slice(-2)}:${
        (`00${date.getSeconds()}`).slice(-2)}`

      return dateStr
    },

    getStatusVariant(estado) {
      switch (estado) {
        case 'En Proceso':
          return 'info'
        case 'Aceptado':
          return 'success'
        case 'Declinado':
          return 'danger'
        case 'Correcciones':
          return 'warning'
        case 'Pedido':
          return 'dark'
        case 'Venta Caída':
          return 'danger'
        default:
          return 'secondary'
      }
    },

    changeStatus(estado) {
      let alertMsg = ''
      let successMsg = ''

      switch (estado) {
        case 'Enviado':
          alertMsg = '¿Está seguro de enviar la cotización al cliente?'
          successMsg = 'Cotización enviada correctamente.'
          break
        case 'Aceptado':
          alertMsg = '¿Está seguro de marcar la cotización como aceptada por el cliente?'
          successMsg = 'Cotizacion marcada como aceptada correctamente.'
          break
        case 'Declinado':
          alertMsg = '¿Está seguro de rechazar la cotización?'
          successMsg = 'Cotizacion declinada correctamente.'
          break
        case 'Correcciones':
          alertMsg = '¿Está seguro de marcar la cotización en proceso correcciones?'
          successMsg = 'Marcada como en proceso de correcciones, correctamente.'
          break
        case 'Pedido':
          alertMsg = '¿Está seguro de generar el pedido basado en esta cotización?'
          successMsg = 'Pedido generado correctamente.'
          break
        default:
          alertMsg = '¿Está seguro de realizar esta acción?'
          successMsg = 'Acción realizada correctamente.'
          break
      }

      this.$bvModal
        .msgBoxConfirm(alertMsg, {
          title: 'Atención',
          size: 'sm',
          okVariant: 'primary',
          cancelVariant: 'outline-dark',
          okTitle: 'SI',
          cancelTitle: 'NO',
          okTitleVariant: 'outline-danger',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            axios
              .put(`customer-quote/${this.quoteId}/status/${estado}`)
              .then(response => {
                if (response.data.res) {
                  this.makeToast('success', 'Actualizado', successMsg)
                  this.getCustomerQuoteData()
                } else {
                  throw (response)
                }
              })
              .catch(error => {
                const errorData = error.response?.data || error.data || error

                if (estado === 'Pedido' && errorData?.stock_issues) {
                  this.stockIssues = errorData.stock_issues
                  this.stockSummary = errorData.summary || null
                  this.$bvModal.show('modal-stock-issues')
                } else if (!error.response && errorData?.res === false && errorData?.msg) {
                  // Rechazo de la conversion con su mensaje (por ejemplo, el minimo
                  // de unidades de PRECIO VOLUMEN). showErrors no lo leeria: no es
                  // un error de axios sino la respuesta misma.
                  this.makeToast('danger', 'No se puede generar el pedido', errorData.msg)
                } else {
                  this.showErrors(error)
                }
              })
          }
        })
    },

    // Conversión parcial: el pedido se crea con lo disponible y el faltante se registra como venta caída
    convertPartial(bvModalEvent) {
      if (bvModalEvent && typeof bvModalEvent.preventDefault === 'function') {
        bvModalEvent.preventDefault()
      }
      if (this.isConvertingPartial) return

      this.isConvertingPartial = true
      axios
        .put(`customer-quote/${this.quoteId}/status/Pedido`, { accept_partial: true })
        .then(response => {
          if (response.data.res) {
            const conversion = response.data.conversion || {}
            this.$bvModal.hide('modal-stock-issues')
            this.stockIssues = null
            this.stockSummary = null
            this.makeToast(
              conversion.order_id ? 'success' : 'warning',
              conversion.order_id ? 'Pedido generado' : 'Venta caída registrada',
              response.data.msg,
            )
            this.getCustomerQuoteData()
          } else {
            throw (response)
          }
        })
        .catch(error => {
          const errorData = error.response?.data || error.data || error
          if (!error.response && errorData?.res === false && errorData?.msg) {
            this.$bvModal.hide('modal-stock-issues')
            this.makeToast('danger', 'No se puede generar el pedido', errorData.msg)
          } else {
            this.showErrors(error)
          }
        })
        .finally(() => {
          this.isConvertingPartial = false
        })
    },

    userData() {
      return JSON.parse(localStorage.getItem('DuttonAuthUser'))
    },

    addComment() {
      this.disabledAction = 'disabled'
      if (this.commentText.length > 0) {
        const comment = {
          comment: this.commentText,
        }

        axios
          .post(`comments/quote/${this.quoteId}`, comment)
          .then(response => {
            if (response.data.res) {
              this.makeToast('success', 'Comentario agregado', 'Comentario agregado correctamente.')
              this.commentText = ''
              this.disabledAction = 'enabled'
              this.getCustomerQuoteData()
            } else {
              throw (response)
            }
          })
          .catch(error => {
            this.showErrors(error)
          })
      }
    },

    getEditButtonTooltip(estado) {
      switch (estado) {
        case 'En Proceso':
          return 'Editar cotización en proceso'
        case 'Correcciones':
          return 'Realizar correcciones solicitadas'
        case 'Aceptado':
          return 'Editar cambios finales antes de generar pedido (el faltante de stock se registra como venta caída al convertir)'
        default:
          return 'Editar cotización'
      }
    },

    getImageUrl(path) {
      if (!path) return NO_IMAGE_URL
      return buildStorageUrl(path)
    },

    handleImageError(event) {
      const img = event && event.target
      if (img) img.src = NO_IMAGE_URL
    },

  },
}
</script>

<style lang="scss">

td,
th,
tr,
table {
    border-top: 1px solid black;
    border-collapse: collapse;
}

td.producto,
th.producto {
    width: 145px;
    max-width: 145px;
}

td.cantidad,
th.cantidad {
    width: 110px;
    max-width: 110px;
    word-break: break-all;
}

td.precio,
th.precio {
    width: 70px;
    max-width: 70px;
    word-break: break-all;
}

.centrado {
    text-align: center;
    align-content: center;
}

.ticket {
    width: 240px;
    max-width: 240px;
    font-size: 10px;
}

img {
    max-width: inherit;
    width: inherit;
}

.total_size {
  font-size:15px
}

.status-flow-diagram {
  .status-row {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 10px 0;

    .status-box {
      padding: 10px 15px;
      border-radius: 5px;
      text-align: center;
      font-weight: bold;
      min-width: 120px;

      &.info {
        background-color: #00ccff1a;
        color: #00ccff;
      }

      &.secondary {
        background-color: #82868b1a;
        color: #82868b;
      }

      &.success {
        background-color: #28c76f1a;
        color: #28c76f;
      }

      &.warning {
        background-color: #ff9f431a;
        color: #ff9f43;
      }

      &.danger {
        background-color: #ea54551a;
        color: #ea5455;
      }

      &.dark {
        background-color: #4b4b4b1a;
        color: #4b4b4b;
      }

      small {
        font-size: 0.75rem;
        font-weight: normal;
      }
    }

    .arrow {
      margin: 0 15px;
      color: #82868b;
      font-weight: bold;
    }
  }
}
</style>
